import { forwardRef, Ref, useState } from 'react';
import Grid, {
    GridExposedMethods,
    type GridSettings,
    type OnViewChangedCallback,
} from '~/components/Grid';
import { useReportQuery } from '~/lib/Queries';
import type { ReportResponse } from '~/lib/Api.types';
import { ReportType, TemplateIndex } from '../../ReportsIndexPage/Report.types';
import useColumnsFromKeys from './useColumnsFromKeys';
import EventDetailsPanel from '~/components/calendar/EventDetailsPanel';
import SubmissionDetailsPanel from '~/components/forms/submissions/SubmissionDetailsPanel';

type GenericDataGridProps<ReportObject = any> = {
    initialSettings?: GridSettings;
    onViewChanged?: OnViewChangedCallback<ReportObject>;
    templateSelected?: TemplateIndex;
    type: ReportType;
};

const GenericDataGrid = <ReportObject extends { [key: string]: any }>(
    {
        initialSettings,
        onViewChanged,
        templateSelected,
        type,
    }: GenericDataGridProps,
    ref: Ref<GridExposedMethods>,
) => {
    const [selectedEvent, setSelectedEvent] = useState<{
        id: number;
    } | null>();
    const [selectedFormSubmission, setSelectedFormSubmission] = useState<{
        id: number;
    } | null>();
    const {
        data: clientsReport,
        isLoading,
        isFetchingNextPage,
    } = useReportQuery(type);
    const columns = useColumnsFromKeys<ReportResponse<ReportObject>>(
        clientsReport,
        type,
        templateSelected,
        { setSelectedEvent, setSelectedFormSubmission },
    );
    return (
        <>
            <EventDetailsPanel
                event={selectedEvent ?? undefined}
                isOpen={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
            />

            <SubmissionDetailsPanel
                submission={selectedFormSubmission ?? undefined}
                isOpen={!!selectedFormSubmission}
                onClose={() => setSelectedFormSubmission(null)}
                refetch={() => null}
            />

            <Grid<ReportObject>
                isFetchingMore={isFetchingNextPage}
                ref={ref}
                columns={columns}
                rowData={(clientsReport as ReportResponse<ReportObject>)?.data}
                initialSettings={initialSettings}
                onViewChanged={onViewChanged}
                isLoading={isLoading}
            />
        </>
    );
};
export default forwardRef(GenericDataGrid) as <TData = any>(
    props: GenericDataGridProps<TData> & { ref?: Ref<GridExposedMethods> },
) => JSX.Element;
